import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Checkbox, Input, Loader, Modal } from "ui";

import { TicketReturnItem, TicketReturnMedia } from "../../aiTickets.types";
import {
  useCreateManualReturn,
  useCreateReturnFromTicket,
  useManualReturnOrderContext,
  useTicketReturnContext,
} from "../../apis";

import styles from "./CreateReturnModal.module.scss";

/**
 * Where the form gets its data and where it writes back. A ticket-backed return
 * is a second step on something the assistant already opened; a manual one has
 * no ticket at all and writes the return in a single call, so nothing is left
 * half-created if the agent changes their mind.
 */
export type ReturnFormSource =
  | { kind: "ticket"; ticketId: string }
  | { kind: "manual"; conversationId: string; orderId: string };

interface Props {
  source: ReturnFormSource;
  title?: string;
  defaultReason: string;
  defaultDescription: string | null;
  isOpen: boolean;
  onClose: () => void;
}

// Items of one pack instance go back together or not at all — the server
// rejects a partial pack (PartialPackReturnNotAllowed), so ticking one line of
// a pack ticks the rest.
const packMatesOf = (
  items: TicketReturnItem[],
  item: TicketReturnItem,
): TicketReturnItem[] =>
  item.packGroupId
    ? items.filter((other) => other.packGroupId === item.packGroupId)
    : [item];

const returnableQuantity = (item: TicketReturnItem): number =>
  item.quantity - item.returnQuantity;

// A delivery address created from a WhatsApp order holds the bare digits Meta
// sends ("2250700000000"); a refund destination has to be "+2250700000000".
// Shown normalized so the agent sees the number the payout will actually use.
// A national-format number (leading 0) is left alone — the country would be a
// guess, and the server rejects it rather than sending a refund somewhere else.
const toE164 = (phone: string): string => {
  const trimmed = phone.trim();
  if (trimmed.startsWith("+")) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  return !digits || digits.startsWith("0") ? trimmed : `+${digits}`;
};

export const CreateReturnModal = ({
  source,
  title,
  defaultReason,
  defaultDescription,
  isOpen,
  onClose,
}: Props) => {
  const { t } = useTranslation();

  const isTicket = source.kind === "ticket";

  const ticketContext = useTicketReturnContext(
    isOpen && isTicket ? source.ticketId : null,
  );
  const manualContext = useManualReturnOrderContext(
    isOpen && !isTicket ? source.conversationId : null,
    isOpen && !isTicket ? source.orderId : null,
  );
  const { data: context, isLoading } = isTicket ? ticketContext : manualContext;

  const [selectedBarcodeIds, setSelectedBarcodeIds] = useState<string[]>([]);
  const [selectedMessageIds, setSelectedMessageIds] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [mobileMoneyNumber, setMobileMoneyNumber] = useState("");

  // Seed from what the assistant gathered: its media picks, the ticket title as
  // the reason, its summary as the description. The agent edits from there
  // instead of retyping the conversation.
  useEffect(() => {
    if (!context) return;

    setSelectedMessageIds(
      context.media.filter((m) => m.isEvidence).map((m) => m.whatsappMessageId),
    );
    setReason((current) => current || defaultReason.slice(0, 100));
    setDescription((current) => current || defaultDescription || "");
    setMobileMoneyNumber(
      (current) =>
        current || toE164(context.deliveryAddress?.phoneNumber ?? "") || "",
    );
  }, [context, defaultReason, defaultDescription]);

  const returnableItems = useMemo(
    () => (context?.items ?? []).filter((item) => returnableQuantity(item) > 0),
    [context],
  );

  const onCreated = () => {
    toast.success(t("Return has been created"));
    onClose();
  };
  const createFromTicket = useCreateReturnFromTicket({ onSuccess: onCreated });
  const createManual = useCreateManualReturn({ onSuccess: onCreated });
  const isSubmitting = createFromTicket.isLoading || createManual.isLoading;

  const toggleItem = (item: TicketReturnItem) => {
    const mates = packMatesOf(returnableItems, item).map((m) => m.barcodeId);
    const isSelected = selectedBarcodeIds.includes(item.barcodeId);

    setSelectedBarcodeIds((current) =>
      isSelected
        ? current.filter((id) => !mates.includes(id))
        : [...new Set([...current, ...mates])],
    );
  };

  const toggleMedia = (media: TicketReturnMedia) => {
    setSelectedMessageIds((current) =>
      current.includes(media.whatsappMessageId)
        ? current.filter((id) => id !== media.whatsappMessageId)
        : [...current, media.whatsappMessageId],
    );
  };

  const submit = () => {
    if (!context) return;

    const shared = {
      returnItems: returnableItems
        .filter((item) => selectedBarcodeIds.includes(item.barcodeId))
        .map((item) => ({
          barcodeId: item.barcodeId,
          quantity: returnableQuantity(item),
        })),
      returnReason: reason.trim(),
      returnDescription: description.trim() || undefined,
      evidenceMessageIds: selectedMessageIds,
      returnMobileMoneyNumber: mobileMoneyNumber.trim() || undefined,
    };

    if (source.kind === "ticket") {
      createFromTicket.mutate({ ticketId: source.ticketId, ...shared });
      return;
    }

    createManual.mutate({
      whatsappConversationId: source.conversationId,
      orderId: source.orderId,
      ...shared,
    });
  };

  const canSubmit =
    !!context &&
    context.isWithinDeadline &&
    selectedBarcodeIds.length > 0 &&
    reason.trim().length >= 3;

  return (
    <Modal
      isOpen={isOpen}
      title={title ?? t("Create return")}
      okText={t("Create return")}
      isLoading={isSubmitting}
      isOkDisabled={!canSubmit}
      className={styles.createReturnModal_dialog}
      overlayClassName={styles.createReturnModal_overlay}
      onClose={onClose}
      onOk={submit}
    >
      {isLoading || !context ? (
        <Loader />
      ) : (
        <div className={styles.createReturnModal_body}>
          <div className={styles.createReturnModal_meta}>
            <div>
              <span className={styles.createReturnModal_metaLabel}>
                {t("Order")}
              </span>
              <span>{context.orderId}</span>
            </div>
            <div>
              <span className={styles.createReturnModal_metaLabel}>
                {t("Reported")}
              </span>
              <span>
                {moment(context.complainedAt).format("DD/MM/YYYY HH:mm")}
              </span>
            </div>
            {context.returnDeadlineAt && (
              <div>
                <span className={styles.createReturnModal_metaLabel}>
                  {t("Return deadline")}
                </span>
                <span>
                  {moment(context.returnDeadlineAt).format("DD/MM/YYYY HH:mm")}
                </span>
              </div>
            )}
          </div>

          {/* The window is judged against when the CUSTOMER complained, so a
              ticket opened in time stays actionable however long it waited. */}
          {!context.isWithinDeadline && (
            <div className={styles.createReturnModal_blocked}>
              {t(
                "The return window had already closed when the customer reported this. A return cannot be created.",
              )}
            </div>
          )}

          <div className={styles.createReturnModal_section}>
            <div className={styles.createReturnModal_sectionTitle}>
              {t("Items to return")}
            </div>
            {returnableItems.length === 0 ? (
              <div className={styles.createReturnModal_empty}>
                {t("Every item of this order is already being returned.")}
              </div>
            ) : (
              returnableItems.map((item) => (
                <label
                  key={item.barcodeId}
                  className={styles.createReturnModal_item}
                >
                  <Checkbox
                    checked={selectedBarcodeIds.includes(item.barcodeId)}
                    onChange={() => toggleItem(item)}
                  />
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className={styles.createReturnModal_itemImage}
                    />
                  )}
                  <div className={styles.createReturnModal_itemBody}>
                    <div>{item.name}</div>
                    <div className={styles.createReturnModal_itemQty}>
                      {t("Quantity")}: {returnableQuantity(item)}
                      {item.packGroupId ? ` · ${t("Pack")}` : ""}
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>

          <div className={styles.createReturnModal_section}>
            <div className={styles.createReturnModal_sectionTitle}>
              {t("Media from the conversation")}
            </div>
            {context.media.length === 0 ? (
              <div className={styles.createReturnModal_empty}>
                {t("The customer has not sent any photo or video.")}
              </div>
            ) : (
              <div className={styles.createReturnModal_mediaGrid}>
                {context.media.map((media) => (
                  <button
                    key={media.whatsappMessageId}
                    type="button"
                    className={
                      selectedMessageIds.includes(media.whatsappMessageId)
                        ? styles.createReturnModal_mediaSelected
                        : styles.createReturnModal_media
                    }
                    onClick={() => toggleMedia(media)}
                  >
                    {media.messageType === "video" ? (
                      <video src={media.url ?? undefined} muted />
                    ) : (
                      <img src={media.url ?? undefined} alt="" />
                    )}
                    <span className={styles.createReturnModal_mediaDate}>
                      {moment(media.receivedAt).format("DD/MM HH:mm")}
                    </span>
                    {/* Not blocked — an agent may genuinely want the same photo
                        on two returns — but never a surprise afterwards. */}
                    {media.usedOnAnotherReturn && (
                      <span className={styles.createReturnModal_mediaUsed}>
                        {t("Used on another return")}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={styles.createReturnModal_section}>
            <Input
              label={t("Reason")}
              value={reason}
              maxLength={100}
              onChange={(e) => setReason(e.target.value)}
            />
            <Input
              asTag="textarea"
              label={t("Description")}
              value={description}
              maxLength={500}
              onChange={(e) => setDescription(e.target.value)}
            />
            {/* Cash on delivery leaves nothing to credit back, so a refund goes
                out by mobile money to whichever number is named here. */}
            <Input
              label={t("Mobile money number for refund")}
              value={mobileMoneyNumber}
              onChange={(e) => setMobileMoneyNumber(e.target.value)}
            />
          </div>

          {context.deliveryAddress && (
            <div className={styles.createReturnModal_address}>
              {t("Pickup at")}: {context.deliveryAddress.fullName},{" "}
              {context.deliveryAddress.address}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CreateReturnModal;
